import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { isImdbOrgUuid, isStandardUuidString, resolveImdbOrgIdForOrderingApi } from '@/utils/orderingOrgId'
import * as Sentry from '@sentry/react'

/** JSON model for GET /database/organizations/{org_id}/patient-by-mrn */
export interface MrnOrganizationLookupResponse {
  mrn_in_use: boolean
  patient_id: string | null
}

async function readErrorDetail(response: Response): Promise<string> {
  try {
    const j = (await response.json()) as { detail?: string; message?: string }
    if (typeof j.detail === 'string' && j.detail) return j.detail
    if (typeof j.message === 'string' && j.message) return j.message
  } catch {
    /* ignore */
  }
  return response.statusText || `Request failed (${response.status})`
}

/**
 * GET /database/organizations/{org_id}/patient-by-mrn?mrn=…
 * Bearer JWT; org_id must match the authenticated user's organization.
 */
export async function lookupMrnInOrganization(
  organizationId: string,
  mrn: string,
  signal?: AbortSignal
): Promise<MrnOrganizationLookupResponse> {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'GET /database/organizations/{org_id}/patient-by-mrn',
    },
    async (span) => {
      const client = ProviderAPIClient.getInstance()
      const path = `/database/organizations/${encodeURIComponent(organizationId)}/patient-by-mrn?mrn=${encodeURIComponent(mrn)}`
      const response = await client.get(path, undefined, true, signal)

      span.setAttribute('http.status_code', response.status)

      if (response.ok) {
        const data = (await response.json()) as MrnOrganizationLookupResponse
        span.setAttribute('mrn_in_use', data.mrn_in_use)
        return data
      }

      const detail = await readErrorDetail(response)
      if (response.status >= 500) {
        Sentry.captureMessage('patient-by-mrn lookup failed', {
          level: 'error',
          extra: { status: response.status, detail },
        })
      }
      throw new Error(detail)
    }
  )
}

/** When `organizationId` is not an IMDB UUID (e.g. Epic client key), resolve via provision JWT. */
export interface MrnOrgResolutionContext {
  orgIdFromAuth: string | null | undefined
  isEpicFlow: boolean
  epicOAuthState: string | null | undefined
  additionalUuidCandidate?: string | null
}

export interface CheckProbandMrnAvailableParams {
  organizationId: string
  mrn: string
  /**
   * Edit flow: when the user leaves the MRN identical to the loaded value, skip the lookup.
   * (Avoids false conflicts when `patient.id` is a non-IMDB identifier such as a FHIR logical id.)
   * Evaluated before org resolution — unchanged MRN is allowed even when IMDB org UUID cannot be resolved (e.g. Epic token issues).
   */
  skipLookupWhenMrnUnchangedFrom?: string | null
  /** IMDB family-member UUID for this proband; allows the same MRN when it already belongs to this patient. */
  currentImdbPatientId?: string | null
  signal?: AbortSignal
  orgResolution?: MrnOrgResolutionContext
}

/**
 * Shown when the MRN already belongs to a different patient in the org.
 *
 * Exported so the new-order modal can recognise this specific error and let the
 * richer existing-patient banner stand in for it, without matching on a copy of
 * the string that would drift the moment the wording changes.
 */
export const MRN_ALREADY_ASSIGNED_MESSAGE =
  'This MRN is already assigned to another patient in your organization. Enter a different MRN or open the existing patient’s order.'

/**
 * Proband-only organizational MRN uniqueness check before persisting demographics.
 *
 * @returns `{ ok: true }` when the MRN may be used; `{ ok: false, message }` for a duplicate in the org.
 */
export async function checkProbandMrnAvailable(
  params: CheckProbandMrnAvailableParams
): Promise<{ ok: true } | { ok: false; message: string }> {
  const mrn = params.mrn.trim()
  if (!mrn) {
    return { ok: false, message: 'MRN must not be empty.' }
  }

  const unchanged = params.skipLookupWhenMrnUnchangedFrom?.trim()
  if (unchanged !== undefined && unchanged !== '' && mrn === unchanged) {
    return { ok: true }
  }

  let orgId = params.organizationId.trim()
  if (!isImdbOrgUuid(orgId) && params.orgResolution) {
    orgId = (
      await resolveImdbOrgIdForOrderingApi({
        orgIdFromAuth: params.orgResolution.orgIdFromAuth,
        additionalUuidCandidate: params.orgResolution.additionalUuidCandidate,
        isEpicFlow: params.orgResolution.isEpicFlow,
        epicOAuthState: params.orgResolution.epicOAuthState ?? null,
      })
    ).trim()
  }

  if (!orgId) {
    return {
      ok: false,
      message: 'Cannot verify MRN uniqueness without organization context. Refresh the page or sign in again.',
    }
  }

  const data = await lookupMrnInOrganization(orgId, mrn, params.signal)
  if (!data.mrn_in_use) {
    return { ok: true }
  }

  const apiPatientId = data.patient_id?.trim() ?? ''
  const current = params.currentImdbPatientId?.trim() ?? ''
  if (apiPatientId && isStandardUuidString(current) && apiPatientId.toLowerCase() === current.toLowerCase()) {
    return { ok: true }
  }

  return {
    ok: false,
    message: MRN_ALREADY_ASSIGNED_MESSAGE,
  }
}
